import React, { useState } from 'react';
import styled from 'styled-components';
import { withUrlValidation } from '../components/withUrlValidation';

const Container = styled.div`
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
  font-family: 'MapoDPP', sans-serif;
`;

const Title = styled.h1`
  color: #333;
  text-align: center;
  margin-bottom: 30px;
`;

const FormContainer = styled.div`
  background: #f5f5f5;
  padding: 30px;
  border-radius: 10px;
  margin-bottom: 30px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #ddd;
  border-radius: 6px;
  font-size: 16px;
  font-family: 'MapoDPP', sans-serif;
  
  &:focus {
    outline: none;
    border-color: #4FFFFF;
  }
`;

const GenerateButton = styled.button`
  background: #4FFFFF;
  color: #333;
  border: none;
  padding: 15px 30px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  width: 100%;
  transition: background 0.3s;

  &:hover {
    background: #3EEEEE;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const UrlSection = styled.div`
  margin-top: 30px;
`;

const SectionTitle = styled.h2`
  color: #333;
  margin-bottom: 15px;
  border-bottom: 2px solid #4FFFFF;
  padding-bottom: 5px;
`;

const UrlCard = styled.div`
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const UrlLabel = styled.div`
  font-weight: bold;
  color: #333;
  margin-bottom: 10px;
  font-size: 16px;
`;

const UrlText = styled.div`
  background: #f8f8f8;
  padding: 15px;
  border-radius: 6px;
  font-family: monospace;
  font-size: 14px;
  word-break: break-all;
  color: #666;
  margin-bottom: 10px;
`;

const CopyButton = styled.button`
  background: #4FFFFF;
  color: #333;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: background 0.3s;

  &:hover {
    background: #3EEEEE;
  }
`;

const ErrorMessage = styled.div`
  color: #ff4444;
  background: #ffe6e6;
  padding: 10px;
  border-radius: 6px;
  margin-top: 10px;
  text-align: center;
`;

// 브라우저 호환 해시 함수
const sha256 = async (message: string): Promise<string> => {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
};

// 시크릿 키
const SECRET_KEY = 'omniscient-readers-viewpoint-secret-key-2025';

const TimeUrlGeneratorPage = () => {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedHour, setSelectedHour] = useState('');
  const [generatedUrls, setGeneratedUrls] = useState<Record<string, string>>({});
  const [error, setError] = useState('');

  const generateHash = async (timestamp: string): Promise<string> => {
    const data = `${timestamp}-${SECRET_KEY}`;
    const hash = await sha256(data);
    return hash.substring(0, 16);
  };

  const generateUrls = async () => {
    if (!selectedDate || !selectedHour) {
      setError('날짜와 시간을 모두 선택해주세요.');
      return;
    }

    try {
      setError('');
      setGeneratedUrls({}); // 이전 결과 초기화
      
      // 선택된 날짜와 시간으로 Date 객체 생성
      const [year, month, day] = selectedDate.split('-').map(Number);
      const [hours] = selectedHour.split(':').map(Number);
      
      
      // 월은 0부터 시작하므로 -1 해줌
      const selectedDateTime = new Date(year, month - 1, day, hours, 0, 0, 0);
      
      // 사람이 읽기 쉬운 형태로 타임스탬프 생성 (YYYYMMDDHH)
      const timestamp = `${year}${String(month).padStart(2, '0')}${String(day).padStart(2, '0')}${String(hours).padStart(2, '0')}`;
      const hash = await generateHash(timestamp);
      const params = `?t=${timestamp}&h=${hash}`;
      
      const baseUrl = window.location.origin;
      
      const urls = {
        '루트 페이지 (/)': `${baseUrl}${params}`
      };
      
      
      setGeneratedUrls(urls);
    } catch (error) {
      setError('URL 생성 중 오류가 발생했습니다.');
      console.error('URL 생성 오류:', error);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('URL이 클립보드에 복사되었습니다!');
    } catch (err) {
      console.error('클립보드 복사 실패:', err);
      alert('클립보드 복사에 실패했습니다.');
    }
  };

  // 현재 날짜를 기본값으로 설정
  React.useEffect(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hour = String(now.getHours()).padStart(2, '0');
    
    setSelectedDate(`${year}-${month}-${day}`);
    setSelectedHour(`${hour}:00`); // HH:MM 형식으로 설정
  }, []);

  return (
    <Container>
      <Title>시간별 URL 생성기</Title>
      
      <FormContainer>
        <FormGroup>
          <Label htmlFor="date">날짜 선택:</Label>
          <Input
            type="date"
            id="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="hour">시간 선택 (시간 단위):</Label>
          <Input
            type="time"
            id="hour"
            value={selectedHour}
            onChange={(e) => {
              // 시간만 선택하고 분은 00으로 고정
              const timeValue = e.target.value;
              if (timeValue) {
                const [hours] = timeValue.split(':');
                setSelectedHour(`${hours}:00`);
              } else {
                setSelectedHour('');
              }
            }}
            step="3600" // 1시간 단위로만 선택 가능
          />
        </FormGroup>
        
        <GenerateButton onClick={generateUrls}>
          URL 생성하기
        </GenerateButton>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </FormContainer>

      {Object.keys(generatedUrls).length > 0 && (
        <UrlSection>
          <SectionTitle>생성된 URL들</SectionTitle>
          {Object.entries(generatedUrls).map(([page, url]) => (
            <UrlCard key={page}>
              <UrlLabel>{page}</UrlLabel>
              <UrlText>{url}</UrlText>
              <CopyButton onClick={() => copyToClipboard(url)}>
                URL 복사
              </CopyButton>
            </UrlCard>
          ))}
        </UrlSection>
      )}
    </Container>
  );
};

export default withUrlValidation(TimeUrlGeneratorPage, {
  requireValidation: false, // URL 검증 건너뛰기
  showCountdown: false
});
